import React, { useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import type { FormFieldProps } from 'semantic-ui-react';
import type { SchemaLocation } from '@axiom/types';
import { CustomSchemaTypes } from '@axiom/types';
import type { z } from 'zod';

import { FormField } from '../FormField/FormField';
import { useGoogleMaps } from '../../../hooks/useGoogleMaps';
import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import type {
  RawDropdownProps,
  RawDropdownValueType,
} from '../Dropdown/RawDropdown';
import { RawDropdown } from '../Dropdown/RawDropdown';
import { DesignSysEnvUtil } from '../../../utils/design-sys-env-util';

export type SchemaLocation = z.infer<typeof SchemaLocation> | null;
export type LocationProps<AsyncConnectorResult = unknown> = {
  disableArrowKeyChange?: RawDropdownProps<AsyncConnectorResult>['disableArrowKeyChange'];
  description?: string;
  disabled?: RawDropdownProps<AsyncConnectorResult>['disabled'];
  id?: RawDropdownProps<AsyncConnectorResult>['id'];
  label?: React.ReactNode;
  name: RawDropdownProps<AsyncConnectorResult>['name'];
  onBlur?: (value?: SchemaLocation) => void;
  onChange?: (value?: SchemaLocation) => Promise<unknown> | void;
  onFocus?: (value?: SchemaLocation) => void;
  placeholder?: RawDropdownProps<AsyncConnectorResult>['placeholder'];
  showMap?: boolean;
  Tooltip?: React.ReactNode;
};

const noopFn = () => {};

export const Location = <AsyncConnectorResult,>({
  disableArrowKeyChange,
  description,
  disabled,
  id,
  label,
  name,
  onBlur = noopFn,
  onChange = noopFn,
  onFocus = noopFn,
  placeholder,
  showMap,
  Tooltip,
}: LocationProps<AsyncConnectorResult>) => {
  const [mapState, setMapState] = useState<{
    isCentered: boolean;
    isFocused?: boolean;
    predictions: SchemaLocation[];
    valueChanged?: boolean;
  }>({
    isCentered: false,
    isFocused: false,
    predictions: [],
    valueChanged: false,
  });
  const mapCenterLocationIdRef = useRef<string>();
  const mapRef = useRef<HTMLDivElement | null>(null);
  const googleApiService = useGoogleMaps({
    googleApiUrl: DesignSysEnvUtil.googleMapsApi,
    mapRef,
  });
  id = id || name;
  const inValue = (location?: SchemaLocation): RawDropdownValueType =>
    location?.locationPlaceId || '';

  const searchPlace = async (v: string) => {
    return googleApiService.requests
      .getPlaceSuggestions(v)
      .then(predictionLocations => {
        setMapState({
          ...mapState,
          predictions: predictionLocations,
        });
      });
  };
  const debouncedTypeHandler = useMemo(() => debounce(searchPlace, 300), []);

  useEffect(() => {
    if (
      mapRef.current &&
      mapCenterLocationIdRef.current &&
      mapState.isFocused &&
      !mapState.valueChanged &&
      !mapState.isCentered
    ) {
      void googleApiService.requests.getPlaceDetails(
        mapCenterLocationIdRef.current
      );
      setMapState({
        ...mapState,
        isCentered: true,
      });
    }
  }, [mapRef.current, mapState.isFocused]);

  return (
    <div
      data-test={`${name}-location-wrapper`}
      className={AttrsHelper.formatClassname('location-dropdown')}
    >
      <FormField
        {...{
          description,
          disabled,
          icon: 'waypoint-filled',
          id,
          name,
          label,
          onClear: () => {
            googleApiService.requests.clear();
            void onChange(null);
          },
          Tooltip,
        }}
        renderField={({
          value,
          onBlur: formOnBlur,
          setFieldValue,
          invalid,
          schemaProperty,
        }: FormFieldProps) => {
          if (schemaProperty.type !== CustomSchemaTypes.SchemaLocation) {
            throw new Error(
              'You must provide a SchemaLocation schema type for the LocationTypeahead'
            );
          }

          if (
            !mapState.valueChanged &&
            !mapCenterLocationIdRef.current &&
            value?.locationPlaceId
          ) {
            mapCenterLocationIdRef.current = value.locationPlaceId;
          }

          return (
            <RawDropdown
              disableArrowKeyChange={disableArrowKeyChange}
              displayKey="locationName"
              disabled={disabled}
              hideIcon
              id={id}
              invalid={invalid}
              name={name}
              onBlur={e => {
                setMapState({
                  ...mapState,
                  isFocused: false,
                });
                onBlur(value);
                formOnBlur({ ...e, target: { ...e.target, name } });
              }}
              // @ts-expect-error Needs new type
              onChange={async (
                v: string,
                _,
                valueOption: { locationName: string; locationPlaceId: string }
              ) => {
                const placeDetails =
                  await googleApiService.requests.getPlaceDetails(v);
                // use the label of the user-selected option instead of what
                // google feels like sending in placeDetails.locationName
                const formattedPlaceDetails = {
                  ...placeDetails,
                  locationName: valueOption.locationName,
                };
                void onChange(formattedPlaceDetails);
                setFieldValue(name, formattedPlaceDetails);

                setMapState({
                  ...mapState,
                  valueChanged: true,
                });
              }}
              onType={async typedValue => {
                if (typedValue !== undefined && typedValue !== null) {
                  await debouncedTypeHandler(typedValue as string);
                }
              }}
              onFocus={() => {
                setMapState({
                  ...mapState,
                  isFocused: true,
                });
                onFocus();
              }}
              options={
                value && mapState.predictions.length === 0
                  ? [value]
                  : mapState.predictions
              }
              placeholder={placeholder}
              value={inValue(value)}
              valueKey="locationPlaceId"
            />
          );
        }}
      />
      <div
        ref={mapRef}
        data-test={`${name}-location-map`}
        className={AttrsHelper.formatClassname(
          'location-dropdown-map',
          (showMap || mapState.isFocused || mapState.valueChanged) && 'show-map'
        )}
      />
    </div>
  );
};
